import { useNavigate } from "@tanstack/react-router";
import { IconArrowLeft, IconInfoCircle } from "@tabler/icons-react";
import { AdminPageHeader, Alert, Button, Card, Stack, Switch, Table, Tbody, Td, Text, Th, Thead, Tr } from "@/components/ui";
import { formatFechaHora } from "@/shared/format/fecha";
import { useCambiarProrrateoAutomatico, useProrrateoAutomatico } from "../hooks/use-prorrateo-automatico";

/**
 * Gestor del prorrateo automático por tipo y subtipo de solicitud. Aplica a Contrato Anualizado y
 * Orden de Compra (su documento de SAP ya trae el centro de costo y la cuenta contable de cada
 * línea) y a FB60 de CFDI único y múltiples facturas (el centro de costo sale de la relación clave
 * SAT → centro de costo por sociedad y la cuenta de clave SAT → cuenta contable), así que el
 * prorrateo se arma y confirma sin que el solicitante vea el paso. Si algo lo impide, esa
 * solicitud cae al prorrateo manual con el motivo.
 */
export function AdminProrrateoAutomaticoPage() {
  const navigate = useNavigate();
  const { data: configuraciones } = useProrrateoAutomatico();
  const cambiar = useCambiarProrrateoAutomatico();

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <AdminPageHeader
        title="Prorrateo automático"
        description="Por tipo y subtipo de solicitud · el prorrateo se hace solo, con el centro de costo y la cuenta contable del documento de SAP (contrato anualizado y orden de compra) o de las relaciones por clave SAT (FB60), sin la pantalla de prorrateo."
        actions={
          <Button variant="default" leftSection={<IconArrowLeft size={14} />} onClick={() => void navigate({ to: "/admin" })}>
            Volver
          </Button>
        }
      />
      <Stack gap="md">
        <Alert variant="info" icon={<IconInfoCircle size={16} stroke={1.7} />}>
          Con el interruptor encendido, al terminar de asignar el proveedor (o de capturar los datos generales en
          Invoice) el sistema confirma el prorrateo y la solicitud pasa directo a Documentos. Si un centro de costo o una
          cuenta del documento de SAP no está en el catálogo de la sociedad, si en FB60 alguna clave SAT no tiene un
          centro de costo automático o una cuenta asignada a la sociedad, o alguna validación del prorrateo no pasa, la
          solicitud se queda con el prorrateo para captura manual y el solicitante ve el motivo. El cambio aplica a las
          solicitudes que lleguen a ese paso después de guardarlo.
        </Alert>
        <Card padding="lg">
          <div className="overflow-x-auto">
            <Table verticalSpacing="sm">
              <Thead>
                <Tr>
                  <Th>Tipo de solicitud</Th>
                  <Th>Subtipo</Th>
                  <Th>Prorrateo automático</Th>
                  <Th>Última modificación</Th>
                </Tr>
              </Thead>
              <Tbody>
                {configuraciones.map((c) => {
                  const guardando =
                    cambiar.isPending &&
                    cambiar.variables?.tipoSolicitud === c.tipoSolicitud &&
                    cambiar.variables?.subtipo === c.subtipo;
                  return (
                    <Tr key={`${c.tipoSolicitud}:${c.subtipo}`}>
                      <Td>
                        <Text size="sm" fw={500}>
                          {c.nombreTipo}
                        </Text>
                      </Td>
                      <Td>
                        <Text size="sm">{c.nombreSubtipo}</Text>
                      </Td>
                      <Td>
                        <Switch
                          checked={c.habilitado}
                          disabled={guardando}
                          aria-label={`Prorrateo automático · ${c.nombreTipo} · ${c.nombreSubtipo}`}
                          label={c.habilitado ? "Encendido" : "Apagado"}
                          onCheckedChange={(habilitado) =>
                            cambiar.mutate({ tipoSolicitud: c.tipoSolicitud, subtipo: c.subtipo, habilitado })
                          }
                        />
                      </Td>
                      <Td>
                        <Text size="sm" c="dimmed">
                          {c.actualizadoAt ? formatFechaHora(c.actualizadoAt) : "—"}
                        </Text>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </div>
        </Card>
      </Stack>
    </div>
  );
}
